<script setup lang="ts">
/**
 * DualApprovalBanner — shown inside a flagged ApprovalCard once the first
 * sign-off has been recorded.
 *
 * State rules (spec §2.4):
 *   - signedOffBy[0].approverUserId === currentUserId
 *       → amber "Awaiting second sign-off…" banner; Approve button is hidden upstream.
 *   - signedOffBy[0].approverUserId !== currentUserId
 *       → green-tinted "First approval recorded by {name}…"; Approve button stays active.
 */
import type { SignOffDto } from '@/api/boardTypes';
import { useAuth } from '@/stores/auth';

const props = defineProps<{
  signedOffBy: SignOffDto[];
}>();

const auth = useAuth();

/** The first sign-off entry */
const firstSignOff = () => props.signedOffBy[0];

/**
 * True when the current user is already the first approver —
 * they must wait for someone else to provide the second sign-off.
 */
const isCurrentUserFirstApprover = () => {
  const first = firstSignOff();
  return !!first && first.approverUserId === auth.user?.userId;
};
</script>

<template>
  <div
    v-if="signedOffBy.length > 0"
    data-testid="dual-approval-banner"
    :class="[
      'mx-4 mb-3.5 px-3.5 py-2.5 rounded-md border text-caption font-medium leading-relaxed',
      isCurrentUserFirstApprover()
        ? 'bg-amber-100 border-amber-100 text-amber-700'
        : 'bg-green-100 border-green-100 text-green-700',
    ]"
  >
    <template v-if="isCurrentUserFirstApprover()">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="inline w-3.5 h-3.5 mr-1.5 align-middle"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
      Awaiting second sign-off — you already approved this resident.
      Another board member must provide the second approval.
    </template>
    <template v-else>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="inline w-3.5 h-3.5 mr-1.5 align-middle"
      >
        <path d="M20 6L9 17l-5-5" />
      </svg>
      First approval recorded by
      <strong class="font-semibold">{{ firstSignOff()?.approverName ?? 'a board member' }}</strong>.
      Your approval will grant access.
    </template>
  </div>
</template>
